/**
 * Asset manifest contract (ARCHITECTURE §9). `scripts/build-assets.ts` copies/optimizes art from `design/` into
 * `public/assets/` and writes `public/assets/manifest.json` with this shape. Renderers and UI resolve art ONLY
 * through manifest keys.
 *
 * Key conventions:
 *   head/<characterId>/<expression>   expression: neutral | determined | celebrating | dazed | shocked
 *   glove/<home|away>/<pose>           pose: open | fist | grip | ready | reach
 *   icon/<specialKey>                  e.g. icon/hail_mary (SpecialInfo.key)
 *   prop/<name>                        e.g. prop/trophy, prop/football
 *   stadium/<layer>                    e.g. stadium/sky, stadium/stands, stadium/jumbotron, stadium/field
 *                                      (day aliases); per time of day: stadium/<day|night>/<sky|far|near|
 *                                      jumbotron|lights|field>; menu backdrops stadium/day, stadium/night,
 *                                      stadium/bg; field markings stadium/lettering/*, stadium/yard/*
 *   fx/<name>                          particle textures (dust, impact, spark, confetti/<n>, glow, ring, …)
 *   ui/<name>                          menu art (ui/logo)
 * The full key list is printed by `pnpm --filter @hf/client assets` and checked by scripts/__tests__.
 */
import type { AudioAsset } from '@hf/audio';

export interface ImageAsset {
  /** WebP URL relative to the site root (e.g. "/assets/head/rookie/determined.webp"). */
  url: string;
  /** PNG fallback URL. */
  fallback?: string;
  width: number;
  height: number;
  /** Anchor/pivot in 0..1 (head centre for heads). */
  anchor?: { x: number; y: number };
  /** Direction the art faces (heads + home gloves face right; away gloves are pre-mirrored to face left). */
  facing?: 'left' | 'right';
  /**
   * Heads: collision-circle radius in texture px (identical for every head). Draw a head at
   * `scale = resolvedHeadRadiusYd × screenPxPerYard / radius`, where resolvedHeadRadiusYd is the sim's per-player
   * `headRadius` (HEAD_RADIUS × headScale, or HEAD_RADIUS in normalized/ranked play), so the art always matches
   * the collision circle. Icons: radius of the badge disc (identical for every icon, centred).
   */
  radius?: number;
  /**
   * Named points in 0..1 like `anchor` (glove `wrist`, goalpost `base`/`crossbar`/`top`, jumbotron
   * `screenTL`/`screenBR`). Goalposts are anchored at the ground under the crossbar: put the anchor at (end line, 0).
   */
  points?: Record<string, { x: number; y: number }>;
  /** Seamless repeat direction (stadium sky/far/near/field strips). */
  tile?: 'x' | 'xy';
  /**
   * Texture px per world yard: draw the sprite at `scale = screenPxPerYard / ppu` to get its intended world size
   * (gloves share one scale; ball, goalpost and field match the sim's dimensions; parallax layers carry a suggested
   * size). Heads: `ppu = radius / HEAD_RADIUS` is the BASE head size only — multiply the scale by
   * `headRadius / HEAD_RADIUS` for characters with a headScale ≠ 1 (see `radius`).
   */
  ppu?: number;
}

export interface AssetManifest {
  version: 1;
  images: Record<string, ImageAsset>;
  audio: AudioAsset[];
}

export const EXPRESSIONS = ['neutral', 'determined', 'celebrating', 'dazed', 'shocked'] as const;
export type Expression = (typeof EXPRESSIONS)[number];
export const GLOVE_POSES = ['open', 'fist', 'grip', 'ready', 'reach'] as const;
export type GlovePoseKey = (typeof GLOVE_POSES)[number];

export const headKey = (character: string, expression: Expression): string =>
  `head/${character}/${expression}`;
export const gloveKey = (side: 'home' | 'away', pose: GlovePoseKey): string => `glove/${side}/${pose}`;
export const iconKey = (specialKey: string): string => `icon/${specialKey}`;

export const EMPTY_MANIFEST: AssetManifest = { version: 1, images: {}, audio: [] };

/** The part of `fetch` the loader uses (web: the global fetch; native: RN's fetch or a bundled-asset reader). */
export type ManifestFetch = (url: string) => Promise<{ ok: boolean; json(): Promise<unknown> }>;

const globalFetch: ManifestFetch = (url) => (globalThis as unknown as { fetch: ManifestFetch }).fetch(url);

/** Fetch the manifest; missing manifest (assets not built yet) yields an empty one. */
export const loadManifest = async (
  url = '/assets/manifest.json',
  fetcher: ManifestFetch = globalFetch,
): Promise<AssetManifest> => {
  try {
    const res = await fetcher(url);
    if (!res.ok) return EMPTY_MANIFEST;
    const json = (await res.json()) as AssetManifest;
    return json.version === 1 ? json : EMPTY_MANIFEST;
  } catch {
    return EMPTY_MANIFEST;
  }
};
